/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import Webcam from 'react-webcam';
import { RACModal, RACButton, makeStyles, Typography } from '@rentacenter/racstrap';
import { addReceipt } from '../../api/addReceipt';
import { useHistory } from 'react-router-dom';
import { AppRoute } from '../../config/route-config';
import { CameraCaptureModalProps } from '../../types/types';
import { LoadingOverlay } from '../../common/LoadingOverlay/LoadingOverlay';

const FACING_MODE_ENVIRONMENT = 'environment';

export const useCaptureStyles = makeStyles((theme: any) => ({
  dialogContent: {
    textAlign: 'center',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'auto',
  },
  dialog: {
    '& .MuiDialog-paperScrollPaper': {
      width: theme.typography.pxToRem(800),
      maxWidth: theme.typography.pxToRem(800),
      minWidth: theme.typography.pxToRem(600),
      maxHeight: theme.typography.pxToRem(600),
      borderRadius: theme.typography.pxToRem(12),
      overflowY: 'auto',
      margin: '0 auto',
    },
  },
  preview: {
    width: 'auto',
    height: theme.typography.pxToRem(360),
    objectFit: 'cover',
    maxWidth: 'none',
    maxHeight: theme.typography.pxToRem(360),
    margin: 0,
    padding: 0,
  },
  controlsTop: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.typography.pxToRem(8),
    marginBottom: theme.typography.pxToRem(8),
  },
  heading: {
    textAlign: 'center',
    width: '100%',
    margin: `${theme.typography.pxToRem(16)} 0`,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.typography.pxToRem(16),
    marginTop: theme.typography.pxToRem(16),
  },
  rotateButton: {
    minWidth: 'auto',
    padding: theme.typography.pxToRem(8),
    '& svg': {
      width: theme.typography.pxToRem(24),
      height: theme.typography.pxToRem(24),
      transform: 'scaleX(-1)',
    },
  },
}));

export const CameraCaptureModal = ({ mpoNumber, rmsItemNumber, isOpen, onClose, onSave ,manualPurchaseOrderId }: CameraCaptureModalProps) => {
  const classes = useCaptureStyles();
  const history = useHistory();
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const webcamRef = useRef<Webcam | null>(null);
  const [isCameraAvailable, setIsCameraAvailable] = useState<boolean>(true);
  const [deviceId, setDeviceId] = useState<string | null>(null);

  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(true);

  // Device detection: true for mobile only, tablets treated like desktop
  const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && !/iPad/i.test(navigator.userAgent);
  const isTablet = /iPad/i.test(navigator.userAgent) || (navigator.userAgent.includes('Android') && !navigator.userAgent.includes('Mobile'));

  // Flexible constraints, no 'exact', only 'ideal'
  function getVideoConstraints(devId: string | null) {
    if (isMobile || isTablet) {
      return {
        facingMode: FACING_MODE_ENVIRONMENT,
        width: { ideal: 1280 },
        height: { ideal: 720 },
      };
    } else if (devId) {
      return {
        deviceId: { ideal: devId },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      };
    } else {
      return {
        width: { ideal: 1280 },
        height: { ideal: 720 },
      };
    }
  }
  const videoConstraints = getVideoConstraints(deviceId);



  const requestCameraAccess = () => {
    if (!isMounted) return;
    
    setError(null);
    setPermissionDenied(false);
    
    navigator.mediaDevices.getUserMedia({ video: getVideoConstraints(deviceId) })
      .then(() => {
        if (!isMounted) return;
        setIsCameraAvailable(true);
        setError(null);
        setPermissionDenied(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setPermissionDenied(true);
          setIsCameraAvailable(false);
          setError('Camera permission denied. Please allow camera access and try again.');
        } else if (err.name === 'OverconstrainedError') {
          // Fallback: try with no deviceId or facingMode
          navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } } })
            .then(() => {
              if (!isMounted) return;
              setIsCameraAvailable(true);
              setDeviceId(null);
              setError(null);
              setPermissionDenied(false);
            })
            .catch((err2) => {
              if (!isMounted) return;
              setIsCameraAvailable(false);
              if (err2.name === 'NotAllowedError' || err2.name === 'PermissionDeniedError') {
                setPermissionDenied(true);
                setError('Camera permission denied. Please allow camera access and try again.');
              } else {
                setError(`Failed to access camera. Please check device settings.`);
              }
            });
        } else {
          setIsCameraAvailable(false);
          setError(`Failed to access camera. Please check device settings.`);
        }
      });
  };

  useEffect(() => {
    if (!isOpen) return;
    
    setIsMounted(true);
    requestCameraAccess();

    // List video input devices and prioritize rear camera
    navigator.mediaDevices.enumerateDevices()
      .then((allDevices) => {
        if (!isMounted) return;
        const videoDevices = allDevices.filter(d => d.kind === 'videoinput');
        if (videoDevices.length > 0) {
          // Try to find rear camera first
          const rearCamera = videoDevices.find(device => 
            device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('rear') ||
            device.label.toLowerCase().includes('environment')
          );
          setDeviceId(rearCamera ? rearCamera.deviceId : videoDevices[0].deviceId);
        } else {
          setIsCameraAvailable(false);
          setError('No camera detected. Please ensure a camera is available.');
        }
      });

    return () => {
      setIsMounted(false);
      // Clean up webcam stream
      if (webcamRef.current?.stream) {
        const stream = webcamRef.current.stream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !permissionDenied) return;
    
    const interval = setInterval(() => {
      if (isMounted && permissionDenied) {
        requestCameraAccess();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [permissionDenied, isOpen, isMounted]);

  const handleTakePhoto = async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setPreview(imageSrc);
      setFile(null); // No File object, just base64
      // Extract base64 part from data URL
      const base64Data = imageSrc.split(',')[1];
      try {
        setError(null);
        setIsUploading(true);
        
        // call API to upload receipt
        const storeNumber = sessionStorage.getItem('storeNumber') || '';
        await addReceipt({
          manualPurchaseOrderId,
          mpoNumber,
          imageBase64: base64Data,
          storeNumber
        });

        // notify parent (pass the base64 and ids)
        onSave(base64Data, mpoNumber, rmsItemNumber,manualPurchaseOrderId);

        // navigate to PO list page immediately after successful upload
        history.push(AppRoute.POs);
      } catch (err) {
        console.error('Failed to upload receipt:', err);
        setError('Failed to upload receipt. Please try again.');
      } finally {
        setIsUploading(false);
      }
    } else {
      setError('Failed to capture photo. Please try again.');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setFile(null);
      setError(null);
      setPermissionDenied(false);
      setIsCameraAvailable(true);
    }
  }, [isOpen]);

  const handleCloseAndRedirect = () => {
    history.push(AppRoute.POs);
  };

  return (
    <>
      {isUploading && <LoadingOverlay />}
      <RACModal
        isOpen={isOpen}
        classes={{ dialogContent: classes.dialogContent, dialog: classes.dialog }}
        maxWidth={false}
        title={`Capture Receipt for #MPO`}
        content={
        <>
          {error && (
            <Typography variant="body2" color="error" style={{ marginBottom: '1rem' }}>
              {error}
            </Typography>
          )}
          {preview ? (
            <img src={preview} alt="Receipt preview" className={classes.preview} />
          ) : isCameraAvailable ? (
            <div>
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                style={{ width: 700, height: 360, borderRadius: 8 }}
                onUserMediaError={(err) => {
                  setIsCameraAvailable(false);
                  setError(`Camera access failed: Please check settings.`);
                }}
              />
              <Typography variant="body1" style={{ textAlign: 'center', marginTop: 8 }}>
                Place the receipt in the frame and take a photo
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                {deviceId ? 'Using rear camera' : ''}
              </Typography>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16 }}>
                <RACButton variant="contained" color="primary" onClick={handleTakePhoto}>
                  Take Photo
                </RACButton>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Typography variant="body1">
                {permissionDenied ? 'Please allow camera access in your browser to continue' : 'Camera not available'}
              </Typography>
            </div>
          )}
        </>
      }
      onClose={handleCloseAndRedirect}
      buttons={<></>}
    />
    </>
  );
};